import React, { useEffect, useState } from 'react';
import { useStyles } from './SessionTree.styles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import TooltipOne from '../../components/TooltipOne/TooltipOne.component';
import { useDrag, useDrop } from 'react-dnd';
import { WorkMode, NewTreeItemId, NewTreeItemsIds, SessionTreeItemProps } from './SessionTree.types';
import { BreadcrumbLevel } from '../DialogBreadcrumbs/DialogBreadcrumbs.types';
import { IDragItem, Scheme, Task, TaskGroup, ExamSheet, Exam, sessionStatusType } from '../../models/session.types';
import { EditSchemeScoresDetailsErrorPrompt } from '../EditSession/EditSchemeScoresDetailsErrorPrompt';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';

const SessionTreeItem: React.FC<SessionTreeItemProps> = (props) => {
  const classes = useStyles();

  const [isExpanded, setIsExpanded] = useState<boolean>(props.expandedItems.indexOf(props.data.id) !== -1);
  const [isEditSchemeScoresDetailsErrorPromptOpen, setIsEditSchemeScoresDetailsErrorPromptOpen] =
    useState<boolean>(false);

  const isExpandedChanged = props.expandedItems.indexOf(props.data.id) !== -1;
  useEffect(() => {
    setIsExpanded(isExpandedChanged);
  }, [isExpandedChanged]);

  const handleExpandedItems = () => {
    if (!props.expandedItems) {
      return;
    }
    if (props.expandedItems.indexOf(props.data.id) === -1) {
      props.setExpandedItems([...props.expandedItems, props.data.id]);
      setIsExpanded(true);
    } else {
      if (props.level === BreadcrumbLevel.TASK) {
        props.setExpandedItems(
          props.expandedItems.filter((id: string) => id !== props.data.id && id !== props.taskGradeRulesId),
        );
        setIsExpanded(false);
      } else {
        props.setExpandedItems(props.expandedItems.filter((id: string) => id !== props.data.id));
        setIsExpanded(false);
      }
    }
  };

  const renderElementItemTree = () => {
    let item = '';
    switch (props.level) {
      case BreadcrumbLevel.EXAM:
        item =
          props.data.id !== NewTreeItemId.Exam
            ? (props.data as Exam).name + '-' + (props.data as Exam).code
            : 'Zdefiniuj egzamin';
        break;
      case BreadcrumbLevel.EXAMSHEET:
        item =
          props.data.id !== NewTreeItemId.ExamSheet
            ? (props.data as ExamSheet).name + '-' + (props.data as ExamSheet).code
            : 'Zdefiniuj arkusz';
        break;
      case BreadcrumbLevel.TASKGROUP:
        item = props.data.id !== NewTreeItemId.TaskGroup ? (props.data as TaskGroup).name : 'Zdefiniuj grupę zadań';
        break;
      case BreadcrumbLevel.TASK:
        item =
          props.data.id !== NewTreeItemId.Task ? 'Zadanie ' + (props.data as Task).taskNumber : 'Zdefiniuj zadanie';
        break;
      case BreadcrumbLevel.GRADERULES:
        item = 'Zasady oceniania';
        break;
      case BreadcrumbLevel.CRITERIA_SETUP:
        item = 'Kryteria zależne';
        break;
      case BreadcrumbLevel.SCHEMA:
        item =
          props.data.id !== NewTreeItemId.SchemeScores ? (props.data as Scheme).label : 'Zdefiniuj schemat punktowania';
        break;
      default:
        item = '-';
        break;
    }
    return item;
  };

  const { dragType, dropType, onDropTreeItem, canDropTreeItem } = props;
  let isDragging = false;
  let dragRef = null;
  let isOver = false;
  let canDrop = false;
  let dropRef = null;

  if (dragType) {
    [{ isDragging }, dragRef] = useDrag(
      () => ({
        type: dragType,
        item: {
          data: props.data,
          dragData: props.dragData,
        },
        collect: (monitor) => ({
          isDragging: !!monitor.isDragging(),
        }),
        canDrag: () => {
          return !props.dragDisabled && !(props.dragData?.taskGroupStatus !== sessionStatusType.NEW);
        },
      }),
      [props.data],
    );
  }
  if (dropType && onDropTreeItem) {
    [{ isOver, canDrop }, dropRef] = useDrop(
      () => ({
        accept: dropType,
        drop: (item: IDragItem) => {
          onDropTreeItem(props.data.id, item.data.id);
        },
        canDrop: (item: IDragItem) => {
          if (props.dropDisabled) {
            return false;
          }
          if (item.dragData.examSheetId !== (props.data as TaskGroup).examSheetId) {
            return false;
          } else {
            // props.data.assessmentSession should always return something, but in case of test data I allow to fdrop items
            return !!canDropTreeItem && canDropTreeItem(props.data);
          }
        },
        collect: (monitor) => ({
          isOver: !!monitor.isOver(),
          canDrop: !!monitor.canDrop(),
        }),
      }),
      [],
    );
  }

  const isNewItem = NewTreeItemsIds.map((id: NewTreeItemId) => id.toString()).includes(props.data.id);

  const isArrowDropDownIconFixed = () => {
    if (
      props.level === BreadcrumbLevel.CRITERIA_SETUP ||
      props.level === BreadcrumbLevel.SCHEMA ||
      (props.level === BreadcrumbLevel.EXAM && !(props.data as Exam).examSheets?.length) ||
      (props.level === BreadcrumbLevel.EXAMSHEET && !(props.data as ExamSheet).taskGroups?.length) ||
      (props.level === BreadcrumbLevel.TASKGROUP && !(props.data as TaskGroup)?.tasks.length) ||
      (props.level === BreadcrumbLevel.TASK && !(props.data as Task)?.gradeRules)
    ) {
      return true;
    } else return false;
  };

  return (
    <>
      <div ref={dropRef}>
        {props.data && (
          <div className={props.activeTreeId === props.data.id ? classes.sessionTreeActiveItem : ''}>
            <div
              style={{
                marginLeft: props.indent,
              }}
            >
              <div
                className={classes.sessionTreeItem}
                style={{
                  opacity: isDragging ? 0.5 : 1,
                  cursor: !props.dragDisabled && dragRef ? 'move' : 'standard',
                  maxWidth: 'calc(100% - 24px)',
                }}
                ref={dragRef}
              >
                <div className={classes.sessionTreeItemTooltipContainer}>
                  {isExpanded ? (
                    <TooltipOne title="Zwiń element" placement="left">
                      <IndeterminateCheckBoxOutlinedIcon
                        className={classes.ArrowDropDownIcon}
                        style={{
                          height: '0.6em',
                          marginTop: '7px',
                          visibility: !isNewItem ? 'visible' : 'hidden',
                        }}
                        onClick={() => {
                          if (isArrowDropDownIconFixed()) return;
                          handleExpandedItems();
                        }}
                        aria-expanded="true"
                      />
                    </TooltipOne>
                  ) : (
                    <TooltipOne title="Rozwiń element" placement="left">
                      <AddBoxRoundedIcon
                        className={classes.ArrowDropDownIcon}
                        style={{
                          height: '0.6em',
                          marginTop: '7px',
                          visibility: !isNewItem ? 'visible' : 'hidden',
                        }}
                        onClick={() => {
                          handleExpandedItems();
                        }}
                        aria-expanded="false"
                      />
                    </TooltipOne>
                  )}
                  <div
                    className={classes.sessionTreeTextItem}
                    onClick={() => {
                      if (props.workMode === WorkMode.ADD) return;
                      props.setWorkMode(WorkMode.VIEW);
                      props.setActiveTreeLevel(props.level);
                      props.setActiveTreeId(props.data.id);
                      props.onActivate();
                    }}
                  >
                    <TooltipOne title={renderElementItemTree()} placement="right">
                      <span
                        style={
                          'isConfigured' in props.data && props.data.isConfigured
                            ? {
                                display: 'inline-block',
                                maxWidth: 'calc(100% - 35px)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }
                            : {}
                        }
                      >
                        {renderElementItemTree()}
                      </span>
                    </TooltipOne>
                    {props.itemTitleIcon}
                  </div>
                </div>
                {!props.isReadOnly() &&
                  !isNewItem &&
                  props.activeTreeId === props.data.id &&
                  (props.editedItemId !== props.data.id ? (
                    <TooltipOne title="Edytuj" placement="top">
                      <EditIcon
                        className={classes.sessionTreeEditIcon}
                        onClick={() => {
                          !(props.level === BreadcrumbLevel.SCHEMA && (props.data as Scheme).isUsedInCriteriaSetups)
                            ? (props.setActiveTreeLevel(props.level),
                              props.setEditedItemId(props.data.id),
                              props.setWorkMode(WorkMode.EDIT))
                            : setIsEditSchemeScoresDetailsErrorPromptOpen(true);
                        }}
                      />
                    </TooltipOne>
                  ) : !props.isSheetConfigured && !props.hideDeleteIcon ? (
                    <TooltipOne title="Usuń" placement="top">
                      <DeleteOutlineIcon className={classes.sessionTreeEditIcon} onClick={props.removeTreeItem} />
                    </TooltipOne>
                  ) : null)}
              </div>
            </div>
          </div>
        )}
        {props.children}
        <div
          className={isOver && canDrop ? classes.dragOverDropValid : ''}
          style={{
            marginLeft: props.indent,
          }}
        ></div>
      </div>
      <EditSchemeScoresDetailsErrorPrompt
        open={isEditSchemeScoresDetailsErrorPromptOpen}
        onClose={() => setIsEditSchemeScoresDetailsErrorPromptOpen(false)}
      />
    </>
  );
};

export default SessionTreeItem;
